/**
 * Apollo Client setup.
 *
 * Ссылки:
 *   - httpLink     — GraphQL HTTP на gateway
 *   - wsLink       — subscriptions через graphql-ws
 *   - authLink     — Authorization: Bearer <accessToken> + language
 *   - errorLink    — на 401/UNAUTHENTICATED → silent refresh + retry once,
 *                    при failed refresh → signOut() + redirect /app/auth
 *
 * Refresh flow (как в iOS KOService.swift + AuthService.RefreshTokensQuery):
 *   errorLink перехватывает UNAUTHENTICATED, дожидается refresh, повторяет
 *   запрос с новым access-токеном.
 */

import { ApolloClient, ApolloLink, InMemoryCache, split, fromPromise } from '@apollo/client';
import { HttpLink } from '@apollo/client/link/http';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient as createWsClient } from 'graphql-ws';
import { onError } from '@apollo/client/link/error';
import { gql } from '@apollo/client';

const GATEWAY_HTTP = import.meta.env.VITE_GATEWAY_HTTP ?? 'https://dev-api.aura-vibe.site/graphql';
const GATEWAY_WS = import.meta.env.VITE_GATEWAY_WS ?? 'wss://dev-api.aura-vibe.site/graphql';

const REFRESH_MUTATION = gql`
  mutation WebRefreshTokens($refreshToken: String!) {
    refreshTokens(refreshToken: $refreshToken) {
      accessToken
      refreshToken
    }
  }
`;

/** Внутренний мьютекс чтобы при параллельных 401 выполнять refresh только один раз. */
let refreshPromise: Promise<string | null> | null = null;

/** Импортируем лениво чтобы избежать циклической зависимости на моменте init. */
async function doRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  const { getStoredRefreshToken, useAuthStore } = await import('@app/store/auth.store');
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  refreshPromise = (async () => {
    try {
      const raw = await fetch(GATEWAY_HTTP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', language: 'ru' },
        body: JSON.stringify({
          query: REFRESH_MUTATION.loc?.source.body,
          variables: { refreshToken },
        }),
      }).then((r) => r.json());

      const tokens = raw?.data?.refreshTokens;
      if (!tokens?.accessToken) return null;
      useAuthStore.getState().refreshAccessToken(tokens.accessToken, tokens.refreshToken);
      return tokens.accessToken as string;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

const httpLink = new HttpLink({
  uri: GATEWAY_HTTP,
  credentials: 'include',
});

const wsLink = new GraphQLWsLink(
  createWsClient({
    url: GATEWAY_WS,
    connectionParams: () => {
      const token = (window as unknown as { __authToken?: string }).__authToken;
      return token ? { authorization: `Bearer ${token}` } : {};
    },
    retryAttempts: 5,
    shouldRetry: () => true,
  })
);

const transportLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink
);

const authLink = new ApolloLink((operation, forward) => {
  const token = (window as unknown as { __authToken?: string }).__authToken;
  const lang = localStorage.getItem('aura_lang') ?? 'ru';
  const headers: Record<string, string> = { language: lang };
  if (token) headers.authorization = `Bearer ${token}`;
  operation.setContext({ headers });
  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const isUnauthenticated = (graphQLErrors ?? []).some(
    (e) => e.extensions?.code === 'UNAUTHENTICATED' || e.message === 'Unauthorized'
  );
  const isNetwork401 =
    networkError && typeof networkError === 'object' && 'statusCode' in networkError &&
    (networkError as { statusCode: number }).statusCode === 401;

  if (isUnauthenticated || isNetwork401) {
    return fromPromise(
      doRefresh().then((newToken) => {
        if (!newToken) {
          // refresh не удался — логаут
          import('@app/store/auth.store').then(({ useAuthStore }) => {
            useAuthStore.getState().signOut();
            if (window.location.pathname.startsWith('/app') && !window.location.pathname.includes('/app/auth')) {
              window.location.assign('/app/auth');
            }
          });
          return null;
        }
        const oldHeaders = operation.getContext().headers ?? {};
        operation.setContext({
          headers: { ...oldHeaders, authorization: `Bearer ${newToken}` },
        });
        return newToken;
      })
    ).flatMap(() => forward(operation));
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.error(`[GraphQL] ${message} @ ${JSON.stringify(locations)} path=${path}`)
    );
  }
  if (networkError && !isNetwork401) {
    console.error(`[Network] ${networkError}`);
  }
  // TODO Phase 2+: Sentry breadcrumbs
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, transportLink]),
  cache: new InMemoryCache({}),
  defaultOptions: {
    query: { fetchPolicy: 'cache-first' },
    watchQuery: { fetchPolicy: 'cache-and-network' },
  },
});
